<script lang="ts" setup>
import Dialog from '@/components/Dialog/Dialog.vue';
import Button from '@/components/buttons/Button.vue';
import { computed, ref } from 'vue';
import { NumericEvaluationParameterDto, TaskTypeDescription } from '@/api/generator';
import EvaluationsManualTasksModalScreen02 from './EvaluationsManualTasksModalScreen02.vue';
import { checkLimits } from '../utils';
import { isEqual, omit } from 'lodash';
import { useFieldValue } from 'vee-validate';

interface EvaluationsManualTasksModalEditProps {
  dtoToEdit?: NumericEvaluationParameterDto;
}

const props = defineProps<EvaluationsManualTasksModalEditProps>();

const taskType = useFieldValue<TaskTypeDescription>('taskType');

const emit = defineEmits<{
  editEvaluationParameterDto: [param: NumericEvaluationParameterDto];
}>();

const overlayOpen = defineModel<boolean>('overlayOpen', {
  required: true,
});
const afterLeaveHandler = () => {};

const editDoneHandler = () => {
  // do comparison to check if configuration has really changed (and no back and forth)
  if (numericEvaluationParameterDto.value) {
    const newValue = omit({ ...numericEvaluationParameterDto.value }, [
      'lowerWarningLimit',
      'lowerFailedLimit',
      'upperWarningLimit',
      'upperFailedLimit',
    ]);

    const oldValue = omit({ ...props.dtoToEdit }, [
      'lowerWarningLimit',
      'lowerFailedLimit',
      'upperWarningLimit',
      'upperFailedLimit',
    ]);

    if (!isEqual(newValue, oldValue)) {
      emit('editEvaluationParameterDto', numericEvaluationParameterDto.value);
    }
    overlayOpen.value = false;
  }
};

const isFormValid = ref(true);

const submitDisabled = computed<boolean>(() => {
  const dto = numericEvaluationParameterDto.value;

  if (
    !dto?.definition ||
    !dto?.limitType ||
    !dto?.evaluationType ||
    !checkLimits(dto.min, dto.max)
  ) {
    return true;
  }

  // Only check form validity if all required fields are present
  return !isFormValid.value;
});

const numericEvaluationParameterDto = ref<NumericEvaluationParameterDto>();
</script>

<template>
  <Dialog v-model="overlayOpen" size="large" @afterLeave="afterLeaveHandler">
    <template #title>{{ $t('EditQuantity') }}</template>

    <template #text>
      <EvaluationsManualTasksModalScreen02
        :dtoToEdit="dtoToEdit"
        :taskType="taskType"
        :slideActive="true"
        @changeNumericEvaluationParameterDto="
          (value: NumericEvaluationParameterDto) => {
            if (value) {
              if (value.definition) {
                numericEvaluationParameterDto = value;
              }
            }
          }
        "
        @validationStateChange="(valid: boolean) => (isFormValid = valid)"
      />
    </template>

    <template #actions>
      <Button variant="text" :label="$t('Cancel')" @click="() => (overlayOpen = false)" />

      <Button
        variant="secondary"
        :label="$t('Save')"
        :disabled="submitDisabled"
        @click="editDoneHandler"
      />
    </template>
  </Dialog>
</template>
